import Konva from "konva";
import React from "react";
import { Circle, Group, Line, Shape } from "react-konva";
import {
  WallPathShape as IWallPathShape,
  WallPathEdge,
  WallDoor,
  WallWindow
} from "../../../../types";
import {
  getWallControlPoint,
  getWallDotPosition,
  sampleBezierSubRange,
  bezierPointAndNormal,
  bezierArcLength,
  bezierParameterAtDistance,
  quadraticBezierPoint
} from "../../../../utils/bezier";
import { getWallSegments, getCurvedWallSegmentParams, getWallDash } from "../../../../utils/wall";
import { getJunctionsForWall } from "../../../../utils/wallJunction";
import { autoMergeWallPaths } from "../../../../utils/autoMergeWallPaths";
import { wallPathToSegments } from "../../../../utils/wallPathSegments";
import {
  DEFAULT_DOOR_COLOR,
  DEFAULT_WALL_COLOR,
  DEFAULT_WALL_THICKNESS,
} from "../../defines";
import { ShapeCommonProps } from "../../types";
import { createWallPath, computeWallPathBounds } from "../../../../utils/shapeFactory";
import { snapToWallEndpoint } from "../../../../utils/snap";
import { useMapCanvasStagePosition } from "../../contexts/MapCanvasContext";
import { useMapEditorActions, useMapEditorUI } from "../../contexts/MapEditorContext";
import { useShape } from "../ShapeContext/useShape";

const BEZIER_SAMPLES = 40;

interface EdgeRenderData {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  dx: number;
  dy: number;
  length: number;
  ux: number;
  uy: number;
  edge: WallPathEdge;
  isCurved: boolean;
  lcx: number;
  lcy: number;
}

const computeEdgeData = (
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  edge: WallPathEdge
): EdgeRenderData => {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const length = Math.sqrt(dx * dx + dy * dy);
  const ux = length > 0 ? dx / length : 0;
  const uy = length > 0 ? dy / length : 0;
  const curvature = edge.curvature || 0;
  const curvatureCenter = edge.curvatureCenter ?? 0.5;
  const isCurved = curvature !== 0;

  let lcx = 0;
  let lcy = 0;
  if (isCurved) {
    const { cx, cy } = getWallControlPoint(
      fromX, fromY, toX, toY, curvature, curvatureCenter
    );
    lcx = cx - fromX;
    lcy = cy - fromY;
  }

  return { fromX, fromY, toX, toY, dx, dy, length, ux, uy, edge, isCurved, lcx, lcy };
};

interface DoorWindowInteraction {
  onDragStart: (e: Konva.KonvaEventObject<DragEvent>) => void;
  onDragMove: (id: string, e: Konva.KonvaEventObject<DragEvent>) => void;
  onDragEnd: (id: string, e: Konva.KonvaEventObject<DragEvent>) => void;
  onWheel: (id: string, e: Konva.KonvaEventObject<WheelEvent>) => void;
}

const renderEdgeDoors = (
  data: EdgeRenderData,
  thickness: number,
  localOffsetX: number,
  localOffsetY: number,
  interaction?: DoorWindowInteraction
): React.ReactNode[] => {
  const { edge, isCurved, dx, dy, length, ux, uy, lcx, lcy } = data;
  if (!edge.doors || edge.doors.length === 0) return [];

  return edge.doors.map((door) => {
    if (door.transparent) return null;
    const pos = door.position;
    const width = door.width;

    if (isCurved) {
      const arcLen = bezierArcLength(0, 0, lcx, lcy, dx, dy);
      const doorCenterDist = pos * arcLen;
      const t = bezierParameterAtDistance(0, 0, lcx, lcy, dx, dy, doorCenterDist);
      const { x, y, nx, ny } = bezierPointAndNormal(0, 0, lcx, lcy, dx, dy, t);
      const tx = -ny;
      const ty = nx;
      const hw = width / 2;
      return (
        <Line
          key={door.id}
          points={[
            x - hw * tx + localOffsetX,
            y - hw * ty + localOffsetY,
            x + hw * tx + localOffsetX,
            y + hw * ty + localOffsetY
          ]}
          stroke={DEFAULT_DOOR_COLOR}
          strokeWidth={thickness / 2}
          lineCap="round"
          hitStrokeWidth={Math.max(thickness, 20)}
          draggable={!!interaction}
          onDragStart={interaction?.onDragStart}
          onDragMove={interaction ? (e) => interaction.onDragMove(door.id, e) : undefined}
          onDragEnd={interaction ? (e) => interaction.onDragEnd(door.id, e) : undefined}
          onWheel={interaction ? (e) => interaction.onWheel(door.id, e) : undefined}
        />
      );
    }

    const centerDist = pos * length;
    return (
      <Line
        key={door.id}
        points={[
          localOffsetX + (centerDist - width / 2) * ux,
          localOffsetY + (centerDist - width / 2) * uy,
          localOffsetX + (centerDist + width / 2) * ux,
          localOffsetY + (centerDist + width / 2) * uy
        ]}
        stroke={DEFAULT_DOOR_COLOR}
        strokeWidth={thickness / 2}
        lineCap="round"
        hitStrokeWidth={Math.max(thickness, 20)}
        draggable={!!interaction}
        onDragStart={interaction?.onDragStart}
        onDragMove={interaction ? (e) => interaction.onDragMove(door.id, e) : undefined}
        onDragEnd={interaction ? (e) => interaction.onDragEnd(door.id, e) : undefined}
        onWheel={interaction ? (e) => interaction.onWheel(door.id, e) : undefined}
      />
    );
  });
};

const renderEdgeWindows = (
  data: EdgeRenderData,
  thickness: number,
  localOffsetX: number,
  localOffsetY: number,
  interaction?: DoorWindowInteraction
): React.ReactNode[] => {
  const { edge, isCurved, dx, dy, length, ux, uy, lcx, lcy } = data;
  if (!edge.windows || edge.windows.length === 0) return [];

  return edge.windows.map((win) => {
    const pos = win.position;
    const width = win.width;

    if (isCurved) {
      const arcLen = bezierArcLength(0, 0, lcx, lcy, dx, dy);
      const winCenterDist = pos * arcLen;
      const t = bezierParameterAtDistance(0, 0, lcx, lcy, dx, dy, winCenterDist);
      const { x, y, nx, ny } = bezierPointAndNormal(0, 0, lcx, lcy, dx, dy, t);
      const tx = -ny;
      const ty = nx;
      const hw = width / 2;
      return (
        <Line
          key={win.id}
          points={[
            x - hw * tx + localOffsetX,
            y - hw * ty + localOffsetY,
            x + hw * tx + localOffsetX,
            y + hw * ty + localOffsetY
          ]}
          stroke={DEFAULT_DOOR_COLOR}
          strokeWidth={thickness - (thickness * 20 / 100)}
          lineCap="butt"
          hitStrokeWidth={Math.max(thickness, 20)}
          draggable={!!interaction}
          onDragStart={interaction?.onDragStart}
          onDragMove={interaction ? (e) => interaction.onDragMove(win.id, e) : undefined}
          onDragEnd={interaction ? (e) => interaction.onDragEnd(win.id, e) : undefined}
          onWheel={interaction ? (e) => interaction.onWheel(win.id, e) : undefined}
        />
      );
    }

    const centerDist = pos * length;
    return (
      <Line
        key={win.id}
        points={[
          localOffsetX + (centerDist - width / 2) * ux,
          localOffsetY + (centerDist - width / 2) * uy,
          localOffsetX + (centerDist + width / 2) * ux,
          localOffsetY + (centerDist + width / 2) * uy
        ]}
        stroke={DEFAULT_DOOR_COLOR}
        strokeWidth={thickness - (thickness * 20 / 100)}
        lineCap="butt"
        hitStrokeWidth={Math.max(thickness, 20)}
        draggable={!!interaction}
        onDragStart={interaction?.onDragStart}
        onDragMove={interaction ? (e) => interaction.onDragMove(win.id, e) : undefined}
        onDragEnd={interaction ? (e) => interaction.onDragEnd(win.id, e) : undefined}
        onWheel={interaction ? (e) => interaction.onWheel(win.id, e) : undefined}
      />
    );
  });
};

const renderEdgeSegments = (
  data: EdgeRenderData,
  thickness: number,
  color: string,
  localOffsetX: number,
  localOffsetY: number,
  edgeIndex: number,
  dashArray?: number[]
): React.ReactNode[] => {
  const { edge, isCurved, dx, dy, length, ux, uy, lcx, lcy } = data;
  const hasDoors = (edge.doors?.length ?? 0) > 0;

  if (isCurved) {
    const segParams = getCurvedWallSegmentParams(edge.doors, 0, 0, lcx, lcy, dx, dy);
    return segParams.map(([tStart, tEnd], i) => {
      const points = sampleBezierSubRange(0, 0, lcx, lcy, dx, dy, tStart, tEnd, BEZIER_SAMPLES);
      // Offset all points
      const offsetPoints = points.map((v, idx) =>
        idx % 2 === 0 ? v + localOffsetX : v + localOffsetY
      );
      return (
        <Line
          key={`edge-${edgeIndex}-seg-${i}`}
          points={offsetPoints}
          stroke={color}
          strokeWidth={thickness}
          lineCap={hasDoors ? "round" : "butt"}
          lineJoin="round"
          dash={dashArray}
        />
      );
    });
  }

  const segments = getWallSegments(edge.doors, length);
  return segments.map(([start, end], i) => (
    <Line
      key={`edge-${edgeIndex}-seg-${i}`}
      points={[
        localOffsetX + start * ux,
        localOffsetY + start * uy,
        localOffsetX + end * ux,
        localOffsetY + end * uy
      ]}
      stroke={color}
      strokeWidth={thickness}
      lineCap={hasDoors ? "round" : "butt"}
      lineJoin="miter"
      dash={dashArray}
    />
  ));
};

export const WallPathShapeComponent = React.forwardRef<
  React.ComponentRef<typeof Group>,
  ShapeCommonProps
>((commonProps, ref) => {
  const { shape, onUpdate, isSelected, isEditing, setIsEditing, otherShapes } = useShape();
  const { deleteShape, setSelectedIds, setShapes, setWallPathSelectedVertexIndex } = useMapEditorActions();
  const { wallPathSelectedVertexIndex } = useMapEditorUI();
  const { stagePosition } = useMapCanvasStagePosition();
  const groupRef = React.useRef<Konva.Group>(null);
  const pathShape = shape as IWallPathShape;
  const { vertices, edges, closed } = pathShape;
  const thickness = pathShape.thickness || DEFAULT_WALL_THICKNESS;
  const color = pathShape.color || DEFAULT_WALL_COLOR;
  const dashArray = getWallDash(pathShape.dashPattern, thickness);
  const lineCapStart = pathShape.lineCapStart || 'butt';
  const lineCapEnd = pathShape.lineCapEnd || 'butt';

  // Clear vertex selection when shape is deselected or editing mode changes
  React.useEffect(() => {
    if (!isSelected || !isEditing) {
      setWallPathSelectedVertexIndex(null);
    }
  }, [isSelected, isEditing]);

  // Override getClientRect so the Transformer uses vertex-based bounds
  // instead of Konva's auto-computed bounds (which include stroke widths of all children)
  React.useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    const half = thickness / 2;
    const bounds = computeWallPathBounds(vertices);
    group.getClientRect = (config?: { skipTransform?: boolean }) => {
      if (config?.skipTransform) {
        return {
          x: -half,
          y: -half,
          width: bounds.width + thickness,
          height: bounds.height + thickness
        };
      }
      return {
        x: pathShape.x - half,
        y: pathShape.y - half,
        width: bounds.width + thickness,
        height: bounds.height + thickness
      };
    };
  }, [vertices, thickness, pathShape.x, pathShape.y]);

  // Exit edit mode when deselected
  React.useEffect(() => {
    if (!isSelected && isEditing) {
      setIsEditing(false);
    }
  }, [isSelected, isEditing, setIsEditing]);

  // Escape exits edit mode (back to selected with bounding box)
  React.useEffect(() => {
    if (!isEditing) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setIsEditing(false);
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isEditing, setIsEditing]);

  // Local drag state for vertex dragging
  const [dragVertexIndex, setDragVertexIndex] = React.useState<number | null>(null);
  const [dragVertexPos, setDragVertexPos] = React.useState<{ x: number; y: number } | null>(null);
  const [dragAlignX, setDragAlignX] = React.useState<number | undefined>(undefined);
  const [dragAlignY, setDragAlignY] = React.useState<number | undefined>(undefined);
  // While the user holds Cmd/Ctrl during an endpoint drag we surface
  // "magnet" markers at every other open wall-path's endpoints. If the
  // drag releases over one of those, we snap-merge the two paths.
  const [cmdHeld, setCmdHeld] = React.useState(false);
  const [snapMergeTarget, setSnapMergeTarget] = React.useState<{
    x: number;
    y: number;
    pathId: string;
  } | null>(null);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      setCmdHeld(e.metaKey || e.ctrlKey);
    };
    const onBlur = () => setCmdHeld(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("blur", onBlur);
    };
  }, []);
  // Flag: drag initiated from midpoint (needs global mouse tracking since no Konva node is being dragged)
  const [midpointDragActive, setMidpointDragActive] = React.useState(false);

  // Refs for midpoint drag to avoid stale closures in event listeners
  const dragVertexPosRef = React.useRef(dragVertexPos);
  dragVertexPosRef.current = dragVertexPos;
  const dragVertexIndexRef = React.useRef(dragVertexIndex);
  dragVertexIndexRef.current = dragVertexIndex;
  const verticesRef = React.useRef(vertices);
  verticesRef.current = vertices;
  const stagePositionRef = React.useRef(stagePosition);
  stagePositionRef.current = stagePosition;
  const onUpdateRef = React.useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  // Global mouse tracking for midpoint-initiated vertex drag
  React.useEffect(() => {
    if (!midpointDragActive) return;

    const onMouseMove = (e: MouseEvent) => {
      const idx = dragVertexIndexRef.current;
      if (idx === null) return;
      const stage = groupRef.current?.getStage();
      if (!stage) return;
      const container = stage.container();
      const rect = container.getBoundingClientRect();
      const sp = stagePositionRef.current;
      const worldX = (e.clientX - rect.left - sp.x) / sp.scale;
      const worldY = (e.clientY - rect.top - sp.y) / sp.scale;

      if (e.altKey) {
        setDragVertexPos({ x: worldX, y: worldY });
        setDragAlignX(undefined);
        setDragAlignY(undefined);
        return;
      }

      const verts = verticesRef.current;
      const otherVerts = verts
        .filter((_, i) => i !== idx)
        .map((v) => ({ x: v.x, y: v.y }));

      const snapResult = snapToWallEndpoint(
        { x: worldX, y: worldY },
        [],
        sp.scale,
        otherVerts
      );

      setDragVertexPos({ x: snapResult.x, y: snapResult.y });
      setDragAlignX(snapResult.alignX);
      setDragAlignY(snapResult.alignY);
    };

    const onMouseUp = () => {
      const pos = dragVertexPosRef.current;
      const idx = dragVertexIndexRef.current;
      if (pos && idx !== null) {
        const verts = verticesRef.current;
        const newVertices = verts.map((v, i) =>
          i === idx ? { x: Math.round(pos.x), y: Math.round(pos.y) } : v
        );
        const bounds = computeWallPathBounds(newVertices);
        onUpdateRef.current({
          vertices: newVertices,
          ...bounds
        } as Partial<IWallPathShape>);
      }
      setDragVertexIndex(null);
      setDragVertexPos(null);
      setDragAlignX(undefined);
      setDragAlignY(undefined);
      setMidpointDragActive(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [midpointDragActive]);

  // Local drag state for curvature control
  const [dragEdgeIndex, setDragEdgeIndex] = React.useState<number | null>(null);
  const [dragCurvatureValues, setDragCurvatureValues] = React.useState<{
    curvature: number;
    curvatureCenter: number;
  } | null>(null);

  const edgeCount = closed ? vertices.length : vertices.length - 1;

  // Get effective vertices accounting for drag state
  const effectiveVertices = React.useMemo(() => {
    if (dragVertexIndex === null || !dragVertexPos) return vertices;
    return vertices.map((v, i) =>
      i === dragVertexIndex ? { ...v, x: dragVertexPos.x, y: dragVertexPos.y } : v
    );
  }, [vertices, dragVertexIndex, dragVertexPos]);

  // Get effective edge accounting for curvature drag
  const getEffectiveEdge = (edgeIdx: number): WallPathEdge => {
    const edge = edges[edgeIdx] || {};
    if (dragEdgeIndex === edgeIdx && dragCurvatureValues) {
      return {
        ...edge,
        curvature: dragCurvatureValues.curvature,
        curvatureCenter: dragCurvatureValues.curvatureCenter
      };
    }
    return edge;
  };

  // Compute virtual wall segments for junction calculation
  const allWalls = React.useMemo(() => {
    const virtualSegments = wallPathToSegments(pathShape);
    const others = (otherShapes || []).filter(
      (s) => (s.type === "wall" || s.type === "wall-path") && !s.hidden
    );
    // Include virtual segments from other wall-paths too
    const otherWallSegments = others.flatMap((s) => {
      if (s.type === "wall") return [s];
      if (s.type === "wall-path") return wallPathToSegments(s as IWallPathShape);
      return [];
    });
    return [...virtualSegments, ...otherWallSegments];
  }, [pathShape, otherShapes]);

  // Junction fills at path endpoints that connect to other walls
  const junctionFills = React.useMemo(() => {
    const virtualSegments = wallPathToSegments(pathShape);
    const fills = virtualSegments.flatMap((seg) =>
      getJunctionsForWall(seg, allWalls)
    );
    // Deduplicate by center position
    const seen = new Set<string>();
    return fills.filter((fill) => {
      const key = `${Math.round(fill.center.x)},${Math.round(fill.center.y)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      // Skip junction at vertices with cornerRadius (the arc handles the transition)
      const isAtRoundedVertex = vertices.some(
        (v) => v.cornerRadius && v.cornerRadius > 0 &&
          Math.abs(v.x - fill.center.x) < 2 && Math.abs(v.y - fill.center.y) < 2
      );
      if (isAtRoundedVertex) return false;
      return true;
    });
  }, [pathShape, allWalls]);

  const junctionPoints = React.useMemo(
    () =>
      junctionFills.map((fill) => ({
        points: fill.points.flatMap((p) => [p.x - pathShape.x, p.y - pathShape.y]),
        color: fill.color
      })),
    [junctionFills, pathShape.x, pathShape.y]
  );

  // Scale-aware sizes: dots get smaller when zoomed in for pixel-perfect precision
  const scale = stagePosition.scale;
  const curvatureDotRadius = Math.max(1.5, 3.5 / scale);
  const curvatureDotStroke = Math.max(0.5, 1 / scale);
  const vertexRadius = Math.max(2, 5 / scale);
  const vertexStroke = Math.max(0.5, 1.5 / scale);
  const dashLine = Math.max(1, 4 / scale);
  const midpointRadius = Math.max(1.5, 4 / scale);

  const canDrag = isSelected && isEditing && !shape.locked;

  // Vertex handles live inside this Group, so they share the group's z-order
  // among siblings. Other walls drawn after us (or with thicker strokes)
  // would otherwise paint over our editing dots and make them unclickable.
  // Bumping the Group to the top of its parent's children while editing
  // keeps the dots reachable. Konva's `moveToTop` is a cheap O(n) reorder
  // on the existing parent's children array.
  React.useLayoutEffect(() => {
    if (!canDrag) return;
    groupRef.current?.moveToTop();
  });

  const getDoorWindowInteraction = (edgeIdx: number, data: EdgeRenderData, kind: "door" | "window"): DoorWindowInteraction | undefined => {
    if (!canDrag) return undefined;
    return {
      onDragStart: (e: Konva.KonvaEventObject<DragEvent>) => {
        e.cancelBubble = true;
      },
      onDragMove: (id: string, e: Konva.KonvaEventObject<DragEvent>) => {
        e.cancelBubble = true;
        // Let Konva handle the visual drag — we compute final position on dragEnd
      },
      onDragEnd: (id: string, e: Konva.KonvaEventObject<DragEvent>) => {
        e.cancelBubble = true;
        const node = e.target;
        const offsetX = node.x();
        const offsetY = node.y();
        // Reset node position so re-render places it correctly
        node.position({ x: 0, y: 0 });

        const edge = edges[edgeIdx] || {};
        const items = kind === "door" ? (edge.doors || []) : (edge.windows || []);
        const item = items.find((d) => d.id === id);
        if (!item) return;

        let newPos = item.position;
        if (data.isCurved) {
          const arcLen = bezierArcLength(0, 0, data.lcx, data.lcy, data.dx, data.dy);
          if (arcLen > 0) {
            const t = bezierParameterAtDistance(0, 0, data.lcx, data.lcy, data.dx, data.dy, item.position * arcLen);
            const pn = bezierPointAndNormal(0, 0, data.lcx, data.lcy, data.dx, data.dy, t);
            const tx = -pn.ny;
            const ty = pn.nx;
            const proj = offsetX * tx + offsetY * ty;
            newPos = Math.max(0.02, Math.min(0.98, item.position + proj / arcLen));
          }
        } else if (data.length > 0) {
          const proj = offsetX * data.ux + offsetY * data.uy;
          newPos = Math.max(0.02, Math.min(0.98, item.position + proj / data.length));
        }

        const updatedItems = items.map((d) =>
          d.id === id ? { ...d, position: newPos } : d
        );
        const newEdges = [...edges];
        newEdges[edgeIdx] = { ...newEdges[edgeIdx], [kind === "door" ? "doors" : "windows"]: updatedItems };
        onUpdate({ edges: newEdges } as Partial<IWallPathShape>);
      },
      onWheel: (id: string, e: Konva.KonvaEventObject<WheelEvent>) => {
        e.evt.preventDefault();
        e.cancelBubble = true;
        const edge = edges[edgeIdx] || {};
        const items = kind === "door" ? (edge.doors || []) : (edge.windows || []);
        const item = items.find((d) => d.id === id);
        if (!item) return;
        const delta = e.evt.deltaY > 0 ? -5 : 5;
        const newWidth = Math.max(5, Math.min(300, item.width + delta));
        const updatedItems = items.map((d) =>
          d.id === id ? { ...d, width: newWidth } : d
        );
        const newEdges = [...edges];
        newEdges[edgeIdx] = { ...newEdges[edgeIdx], [kind === "door" ? "doors" : "windows"]: updatedItems };
        onUpdate({ edges: newEdges } as Partial<IWallPathShape>);
      }
    };
  };

  // Vertex drag handlers
  // Endpoints of OTHER open wall-paths — possible merge targets when the
  // user holds Cmd while dragging one of THIS path's endpoints. Recomputed
  // on every drag move via `otherShapes` reference (refreshed by the
  // editor context whenever shapes change).
  const mergeCandidates = React.useMemo(() => {
    const out: { x: number; y: number; pathId: string }[] = [];
    for (const s of otherShapes ?? []) {
      if (s.type !== "wall-path" || s.id === pathShape.id) continue;
      const wp = s as IWallPathShape;
      if (wp.closed || wp.vertices.length < 2) continue;
      out.push({
        x: wp.vertices[0].x,
        y: wp.vertices[0].y,
        pathId: wp.id
      });
      out.push({
        x: wp.vertices[wp.vertices.length - 1].x,
        y: wp.vertices[wp.vertices.length - 1].y,
        pathId: wp.id
      });
    }
    return out;
  }, [otherShapes, pathShape.id]);

  const findMergeCandidate = (worldX: number, worldY: number) => {
    // 30 screen px in canvas-world units. Generous so the magnet feels
    // sticky but not so large that random nearby paths get hijacked.
    const tolerance = 30 / scale;
    let best: (typeof mergeCandidates)[number] | null = null;
    let bestDist = Infinity;
    for (const c of mergeCandidates) {
      const d = Math.hypot(c.x - worldX, c.y - worldY);
      if (d <= tolerance && d < bestDist) {
        best = c;
        bestDist = d;
      }
    }
    return best;
  };

  const handleVertexDragStart = (index: number) => (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    setDragVertexIndex(index);
  };

  const handleVertexDragMove = (index: number) => (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    const node = e.target;
    const pos = node.position();
    // pos is relative to the Group's position (pathShape.x, pathShape.y)
    const worldX = pos.x + pathShape.x;
    const worldY = pos.y + pathShape.y;

    const isEndpoint = index === 0 || index === vertices.length - 1;

    // Cmd/Ctrl held + dragging an endpoint of an open path: snap to the
    // nearest other open wall-path endpoint when one is in range. The
    // visual "magnet" markers (rendered below) make targets discoverable;
    // the actual merge happens on drop in handleVertexDragEnd.
    if ((e.evt.metaKey || e.evt.ctrlKey) && isEndpoint && !closed) {
      const target = findMergeCandidate(worldX, worldY);
      if (target) {
        setDragVertexPos({ x: target.x, y: target.y });
        setSnapMergeTarget(target);
        node.position({
          x: target.x - pathShape.x,
          y: target.y - pathShape.y
        });
      } else {
        setDragVertexPos({ x: worldX, y: worldY });
        setSnapMergeTarget(null);
      }
      setDragAlignX(undefined);
      setDragAlignY(undefined);
      return;
    }

    // Any other modifier state (or non-endpoint drag) clears the snap
    // target so dropping outside Cmd-mode never accidentally merges.
    setSnapMergeTarget(null);

    // Hold Alt/Option to disable snapping
    if (e.evt.altKey) {
      setDragVertexPos({ x: worldX, y: worldY });
      setDragAlignX(undefined);
      setDragAlignY(undefined);
      return;
    }

    // Collect other vertices of this path as extra snap targets
    const otherVerts = vertices
      .filter((_, i) => i !== index)
      .map((v) => ({ x: v.x, y: v.y }));

    const snapResult = snapToWallEndpoint(
      { x: worldX, y: worldY },
      [],
      scale,
      otherVerts
    );

    setDragVertexPos({ x: snapResult.x, y: snapResult.y });
    setDragAlignX(snapResult.alignX);
    setDragAlignY(snapResult.alignY);

    // If snapped, reposition the Konva node to match
    if (snapResult.snapped) {
      node.position({
        x: snapResult.x - pathShape.x,
        y: snapResult.y - pathShape.y
      });
    }
  };

  const handleVertexDragEnd = (index: number) => (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;

    // Snap-merge: drop landed on another open wall-path's endpoint while
    // Cmd was held. Update the dragged vertex to the target's exact
    // position, then run autoMergeWallPaths — single setShapes commit so
    // undo restores both separate paths in one keystroke.
    if (snapMergeTarget && dragVertexPos) {
      const newVertices = vertices.map((v, i) =>
        i === index
          ? { ...v, x: snapMergeTarget.x, y: snapMergeTarget.y }
          : v
      );
      const bounds = computeWallPathBounds(newVertices);
      const updated: IWallPathShape = {
        ...pathShape,
        vertices: newVertices,
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height
      };
      setShapes((prev) =>
        autoMergeWallPaths(
          prev.map((s) => (s.id === pathShape.id ? updated : s))
        )
      );
    } else if (dragVertexPos) {
      const newVertices = vertices.map((v, i) =>
        i === index ? { ...v, x: Math.round(dragVertexPos.x), y: Math.round(dragVertexPos.y) } : v
      );
      const bounds = computeWallPathBounds(newVertices);
      onUpdate({
        vertices: newVertices,
        ...bounds
      } as Partial<IWallPathShape>);
    }
    setDragVertexIndex(null);
    setDragVertexPos(null);
    setDragAlignX(undefined);
    setDragAlignY(undefined);
    setSnapMergeTarget(null);
  };

  // Curvature control handlers
  const handleCurvatureDragMove = (edgeIdx: number, fromX: number, fromY: number, toX: number, toY: number) =>
    (e: Konva.KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      const node = e.target;
      const pos = node.position();

      const edgeDx = toX - fromX;
      const edgeDy = toY - fromY;
      const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
      if (edgeLen === 0) return;

      const ux = edgeDx / edgeLen;
      const uy = edgeDy / edgeLen;
      const perpX = -uy;
      const perpY = ux;

      // pos is relative to group, so relative to fromVertex
      const localX = pos.x - (fromX - pathShape.x);
      const localY = pos.y - (fromY - pathShape.y);

      const along = localX * ux + localY * uy;
      const perp = localX * perpX + localY * perpY;

      const newCenter = Math.max(0.05, Math.min(0.95, along / edgeLen));
      const newCurvature = perp;

      const clampedAlong = newCenter * edgeLen;
      node.position({
        x: (fromX - pathShape.x) + clampedAlong * ux + newCurvature * perpX,
        y: (fromY - pathShape.y) + clampedAlong * uy + newCurvature * perpY
      });

      setDragEdgeIndex(edgeIdx);
      setDragCurvatureValues({ curvature: newCurvature, curvatureCenter: newCenter });
    };

  const handleCurvatureDragEnd = (edgeIdx: number) =>
    (e: Konva.KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      if (dragCurvatureValues) {
        const newEdges = [...edges];
        newEdges[edgeIdx] = {
          ...newEdges[edgeIdx],
          curvature: Math.round(dragCurvatureValues.curvature),
          curvatureCenter: Math.round(dragCurvatureValues.curvatureCenter * 100) / 100
        };
        onUpdate({ edges: newEdges } as Partial<IWallPathShape>);
      }
      setDragEdgeIndex(null);
      setDragCurvatureValues(null);
    };

  // Alt+Click on vertex: insert a new vertex after it (adds an edge)
  const handleVertexClick = (index: number) => (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (e.evt.altKey) {
      handleVertexInsert(index)(e);
      return;
    }
    e.cancelBubble = true;
    setWallPathSelectedVertexIndex(index);
  };

  const handleVertexInsert = (index: number) => (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!e.evt.altKey) return;
    e.cancelBubble = true;

    const vertexCount = vertices.length;

    // Determine the next vertex index
    const isEndpoint = !closed && index === vertexCount - 1;
    const nextIndex = isEndpoint
      ? index - 1  // For last vertex of open path, insert between it and previous
      : (index + 1) % vertexCount;

    const current = vertices[index];
    const next = vertices[nextIndex];

    // New vertex at midpoint
    const newVertex = {
      x: Math.round((current.x + next.x) / 2),
      y: Math.round((current.y + next.y) / 2)
    };

    // Insert after `index` (or before if at end of open path)
    const insertAt = isEndpoint ? index : index + 1;
    const newVertices = [
      ...vertices.slice(0, insertAt),
      newVertex,
      ...vertices.slice(insertAt)
    ];

    // Split the edge: the edge at `edgeIndex` becomes two edges
    const edgeIndex = isEndpoint ? index - 1 : index;
    const oldEdge = edges[edgeIndex] || {};
    // New edges replace the old one — keep it simple, no curvature on split edges
    const newEdges = [
      ...edges.slice(0, edgeIndex),
      {},
      {},
      ...edges.slice(edgeIndex + 1)
    ];

    const bounds = computeWallPathBounds(newVertices);

    onUpdate({
      vertices: newVertices,
      edges: newEdges,
      ...bounds
    } as Partial<IWallPathShape>);
  };

  // Double-click on vertex: remove it (merge edges). Alt+double-click: split path.
  const handleVertexRemove = (index: number) => (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;

    if (e.evt.altKey) {
      handleVertexSplit(index);
      return;
    }

    const vertexCount = vertices.length;

    // If removing would leave < 2 vertices, delete the whole shape
    if (vertexCount <= 2) {
      setSelectedIds([]);
      deleteShape([pathShape.id]);
      return;
    }

    // For closed paths with exactly 3 vertices, become open with 2
    const newClosed = closed && vertexCount <= 3 ? false : closed;

    const newVertices = vertices.filter((_, i) => i !== index);

    // Rebuild edges by mapping each NEW edge back to the old edge that
    // connects the same pair of (still-present) vertices. The previous
    // approach just filtered out touching edges and padded `{}` at the
    // end — which shifted surviving edge data into the wrong slots,
    // moving curvature/etc. to a different segment than the user set it on.
    const newEdges: WallPathEdge[] = [];
    const expectedEdgeCount = newClosed ? newVertices.length : newVertices.length - 1;

    for (let newEi = 0; newEi < expectedEdgeCount; newEi++) {
      const newFrom = newEi;
      const newTo = (newEi + 1) % newVertices.length;
      // newV_k maps to oldV_k when k < index, else oldV_(k+1)
      const oldFrom = newFrom < index ? newFrom : newFrom + 1;
      const oldTo = newTo < index ? newTo : newTo + 1;
      // edges[i] in the original path connects oldV_i to oldV_((i+1) % vertexCount).
      // If that's the same pair as (oldFrom, oldTo), reuse it; otherwise
      // this is the freshly-merged segment that bridges the removed vertex.
      if ((oldFrom + 1) % vertexCount === oldTo) {
        newEdges.push(edges[oldFrom] || {});
      } else {
        newEdges.push({});
      }
    }

    const bounds = computeWallPathBounds(newVertices);

    onUpdate({
      vertices: newVertices,
      edges: newEdges,
      closed: newClosed,
      ...bounds
    } as Partial<IWallPathShape>);
  };

  // Shift+right-click: split path at vertex (no edge merging)
  const handleVertexSplit = (index: number) => {
    const vertexCount = vertices.length;

    if (closed) {
      // Closed path: open it at this vertex (remove vertex, reorder so the gap is at the ends)
      if (vertexCount <= 3) {
        // Too few vertices to split meaningfully — just remove the vertex
        const newVertices = vertices.filter((_, i) => i !== index);
        const newEdges = edges.filter((_, ei) => ei !== index && ei !== (index - 1 + vertexCount) % vertexCount);
        const expectedEdgeCount = newVertices.length - 1;
        while (newEdges.length < expectedEdgeCount) newEdges.push({});
        while (newEdges.length > expectedEdgeCount) newEdges.pop();
        const bounds = computeWallPathBounds(newVertices);
        onUpdate({ vertices: newVertices, edges: newEdges, closed: false, ...bounds } as Partial<IWallPathShape>);
        return;
      }

      // Reorder vertices starting after `index`, skip the removed vertex
      const reordered: typeof vertices = [];
      const reorderedEdges: WallPathEdge[] = [];
      for (let i = 1; i < vertexCount; i++) {
        const vi = (index + i) % vertexCount;
        reordered.push(vertices[vi]);
      }
      // Edges: edge[vi] connects vertex[vi] → vertex[(vi+1)%n]
      // We skip edges touching `index`: edge[index] and edge[(index-1+n)%n]
      for (let i = 0; i < vertexCount; i++) {
        const ei = (index + i) % vertexCount;
        const eiFrom = ei;
        const eiTo = (ei + 1) % vertexCount;
        if (eiFrom === index || eiTo === index) continue;
        reorderedEdges.push(edges[ei] || {});
      }

      const expectedEdgeCount = reordered.length - 1;
      while (reorderedEdges.length < expectedEdgeCount) reorderedEdges.push({});
      while (reorderedEdges.length > expectedEdgeCount) reorderedEdges.pop();

      const bounds = computeWallPathBounds(reordered);
      onUpdate({ vertices: reordered, edges: reorderedEdges, closed: false, ...bounds } as Partial<IWallPathShape>);
      return;
    }

    // Open path: split into two separate paths at the vertex
    // Path A = vertices[0..index-1], Path B = vertices[index+1..end]
    const vertsA = vertices.slice(0, index);
    const edgesA = edges.slice(0, Math.max(0, index - 1));
    const vertsB = vertices.slice(index + 1);
    const edgesB = edges.slice(index + 1);

    // Delete current path
    setSelectedIds([]);
    deleteShape([pathShape.id]);

    // Create new paths (only if they have >= 2 vertices)
    const newPaths: ReturnType<typeof createWallPath>[] = [];
    if (vertsA.length >= 2) {
      newPaths.push(createWallPath(vertsA, { closed: false, edges: edgesA }));
    }
    if (vertsB.length >= 2) {
      newPaths.push(createWallPath(vertsB, { closed: false, edges: edgesB }));
    }
    if (newPaths.length > 0) {
      setShapes((prev) => [...prev, ...newPaths]);
    }
  };

  // Midpoint mouseDown: insert a new vertex at the edge midpoint and start dragging it
  const handleMidpointMouseDown = (edgeIdx: number) => (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;

    if (e.evt.shiftKey) {
      // Shift+click: bend the edge (add default curvature)
      const from = vertices[edgeIdx];
      const to = vertices[(edgeIdx + 1) % vertices.length];
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const edgeLen = Math.sqrt(dx * dx + dy * dy);
      const defaultCurvature = edgeLen * 0.25;
      const newEdges = [...edges];
      newEdges[edgeIdx] = {
        ...newEdges[edgeIdx],
        curvature: Math.round(defaultCurvature),
        curvatureCenter: 0.5
      };
      onUpdate({ edges: newEdges } as Partial<IWallPathShape>);
      return;
    }

    // Regular mouseDown: insert vertex at midpoint and start dragging it
    const from = vertices[edgeIdx];
    const to = vertices[(edgeIdx + 1) % vertices.length];
    const edge = edges[edgeIdx] || {};

    let newVertex: { x: number; y: number };
    if (edge.curvature) {
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const cp = getWallControlPoint(from.x, from.y, to.x, to.y, edge.curvature, edge.curvatureCenter ?? 0.5);
      const lcx = cp.cx - from.x;
      const lcy = cp.cy - from.y;
      const pt = bezierPointAndNormal(0, 0, lcx, lcy, dx, dy, 0.5);
      newVertex = { x: from.x + pt.x, y: from.y + pt.y };
    } else {
      newVertex = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
    }

    const newVertices = [
      ...vertices.slice(0, edgeIdx + 1),
      newVertex,
      ...vertices.slice(edgeIdx + 1)
    ];
    const newEdges = [
      ...edges.slice(0, edgeIdx),
      {},
      {},
      ...edges.slice(edgeIdx + 1)
    ];

    const bounds = computeWallPathBounds(newVertices);

    // Insert the vertex and immediately enter drag state for it
    const newVertexIndex = edgeIdx + 1;
    setDragVertexIndex(newVertexIndex);
    setDragVertexPos({ x: newVertex.x, y: newVertex.y });
    setMidpointDragActive(true);
    onUpdate({ vertices: newVertices, edges: newEdges, ...bounds } as Partial<IWallPathShape>);
  };

  // Midpoint right-click: split the wall path at this edge
  const handleMidpointRightClick = (edgeIdx: number) => (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.evt.preventDefault();
    e.cancelBubble = true;

    if (closed) {
      // Closed path: open it at this edge (remove the closing edge, reorder so gap is here)
      const reordered = [
        ...vertices.slice(edgeIdx + 1),
        ...vertices.slice(0, edgeIdx + 1)
      ];
      const reorderedEdges = [
        ...edges.slice(edgeIdx + 1),
        ...edges.slice(0, edgeIdx)
      ];
      const expectedEdgeCount = reordered.length - 1;
      while (reorderedEdges.length < expectedEdgeCount) reorderedEdges.push({});
      while (reorderedEdges.length > expectedEdgeCount) reorderedEdges.pop();
      const bounds = computeWallPathBounds(reordered);
      onUpdate({ vertices: reordered, edges: reorderedEdges, closed: false, ...bounds } as Partial<IWallPathShape>);
      return;
    }

    // Open path: split into two separate paths
    const vertsA = vertices.slice(0, edgeIdx + 1);
    const edgesA = edges.slice(0, edgeIdx);
    const vertsB = vertices.slice(edgeIdx + 1);
    const edgesB = edges.slice(edgeIdx + 1);

    setSelectedIds([]);
    deleteShape([pathShape.id]);

    const newPaths: ReturnType<typeof createWallPath>[] = [];
    if (vertsA.length >= 2) {
      newPaths.push(createWallPath(vertsA, { closed: false, edges: edgesA }));
    }
    if (vertsB.length >= 2) {
      newPaths.push(createWallPath(vertsB, { closed: false, edges: edgesB }));
    }
    if (newPaths.length > 0) {
      setShapes((prev) => [...prev, ...newPaths]);
    }
  };

  const handleCurvatureDblClick = (edgeIdx: number) =>
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true;
      const newEdges = [...edges];
      newEdges[edgeIdx] = {
        ...newEdges[edgeIdx],
        curvature: 0,
        curvatureCenter: 0.5
      };
      onUpdate({ edges: newEdges } as Partial<IWallPathShape>);
    };

  return (
    <Group {...commonProps} ref={(node) => {
      groupRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<typeof node>).current = node;
    }}>
      {/* Interior fill — only for closed wall-paths with a fillColor.
       *  Drawn first so the wall strokes (and selection handles) sit above.
       *  Coordinates are made relative to the path's bounding-box origin
       *  because the parent Group already translates by (pathShape.x, y).
       *  For curved edges we sample the bezier so the fill follows the
       *  actual outline instead of cutting a straight chord across each
       *  curve. */}
      {pathShape.closed &&
        pathShape.fillColor &&
        pathShape.vertices.length >= 3 && (() => {
          const FILL_SAMPLES = 20;
          const pts: number[] = [];
          const vs = pathShape.vertices;
          for (let i = 0; i < vs.length; i++) {
            const from = vs[i];
            const to = vs[(i + 1) % vs.length];
            const edge = pathShape.edges[i] || {};
            const curvature = edge.curvature || 0;
            if (curvature === 0) {
              pts.push(from.x - pathShape.x, from.y - pathShape.y);
            } else {
              const { cx, cy } = getWallControlPoint(
                from.x,
                from.y,
                to.x,
                to.y,
                curvature,
                edge.curvatureCenter ?? 0.5
              );
              for (let s = 0; s < FILL_SAMPLES; s++) {
                const t = s / FILL_SAMPLES;
                const p = quadraticBezierPoint(
                  from.x,
                  from.y,
                  cx,
                  cy,
                  to.x,
                  to.y,
                  t
                );
                pts.push(p.x - pathShape.x, p.y - pathShape.y);
              }
            }
          }
          return (
            <Line
              points={pts}
              closed
              fill={pathShape.fillColor}
              opacity={pathShape.fillOpacity ?? 1}
            />
          );
        })()}

      {/* Junction corner fills */}
      {junctionPoints.map((jp, i) => (
        <Line
          key={`junction-${i}`}
          points={jp.points}
          fill={jp.color}
          closed
          listening={false}
        />
      ))}

      {/* Render each edge */}
      {Array.from({ length: edgeCount }).map((_, edgeIdx) => {
        const from = effectiveVertices[edgeIdx];
        const to = effectiveVertices[(edgeIdx + 1) % effectiveVertices.length];
        const edge = getEffectiveEdge(edgeIdx);

        // Shorten edge at vertices with cornerRadius
        const fromCR = pathShape.vertices[edgeIdx]?.cornerRadius || 0;
        const toIdx = (edgeIdx + 1) % effectiveVertices.length;
        const toCR = pathShape.vertices[toIdx]?.cornerRadius || 0;

        let adjFromX = from.x;
        let adjFromY = from.y;
        let adjToX = to.x;
        let adjToY = to.y;

        const edgeDx = to.x - from.x;
        const edgeDy = to.y - from.y;
        const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);

        if (edgeLen > 0 && (fromCR > 0 || toCR > 0)) {
          const eux = edgeDx / edgeLen;
          const euy = edgeDy / edgeLen;
          // Only shorten at start if this vertex has two adjacent edges
          const fromHasTwoEdges = closed || edgeIdx > 0;
          const toHasTwoEdges = closed || (edgeIdx + 1) < effectiveVertices.length - 1;
          // Overlap by 1px so the butt cap hides behind the arc
          const startOffset = fromHasTwoEdges ? Math.max(0, Math.min(fromCR, edgeLen / 2) - 1) : 0;
          const endOffset = toHasTwoEdges ? Math.max(0, Math.min(toCR, edgeLen / 2) - 1) : 0;
          adjFromX = from.x + eux * startOffset;
          adjFromY = from.y + euy * startOffset;
          adjToX = to.x - eux * endOffset;
          adjToY = to.y - euy * endOffset;
        }

        const data = computeEdgeData(adjFromX, adjFromY, adjToX, adjToY, edge);

        // Local offset: adjusted start is in world coords, group is at pathShape.x, pathShape.y
        const localOffsetX = adjFromX - pathShape.x;
        const localOffsetY = adjFromY - pathShape.y;

        return (
          <React.Fragment key={`edge-${edgeIdx}`}>
            {/* Doors */}
            {renderEdgeDoors(data, thickness, localOffsetX, localOffsetY, getDoorWindowInteraction(edgeIdx, data, "door"))}

            {/* Wall segments */}
            {renderEdgeSegments(data, thickness, color, localOffsetX, localOffsetY, edgeIdx, dashArray)}

            {/* Windows */}
            {renderEdgeWindows(data, thickness, localOffsetX, localOffsetY, getDoorWindowInteraction(edgeIdx, data, "window"))}

            {/* Curvature control dot per edge (when selected) */}
            {canDrag && (() => {
              const edgeDx = to.x - from.x;
              const edgeDy = to.y - from.y;
              const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
              if (edgeLen === 0) return null;

              const curvature = edge.curvature || 0;
              const curvatureCenter = edge.curvatureCenter ?? 0.5;
              const dot = getWallDotPosition(0, 0, edgeDx, edgeDy, curvature, curvatureCenter);

              return (
                <>
                  {curvature !== 0 && (
                    <Line
                      points={[
                        localOffsetX + curvatureCenter * edgeDx,
                        localOffsetY + curvatureCenter * edgeDy,
                        localOffsetX + dot.x,
                        localOffsetY + dot.y
                      ]}
                      stroke="#3b82f6"
                      strokeWidth={Math.max(0.5, 1 / scale)}
                      dash={[dashLine, dashLine]}
                      listening={false}
                    />
                  )}
                  <Circle
                    x={localOffsetX + dot.x}
                    y={localOffsetY + dot.y}
                    radius={curvatureDotRadius}
                    fill="#3b82f6"
                    stroke="#fff"
                    strokeWidth={curvatureDotStroke}
                    draggable
                    onDragMove={handleCurvatureDragMove(edgeIdx, from.x, from.y, to.x, to.y)}
                    onDragEnd={handleCurvatureDragEnd(edgeIdx)}
                    onDblClick={handleCurvatureDblClick(edgeIdx)}
                    onDblTap={handleCurvatureDblClick(edgeIdx)}
                  />
                </>
              );
            })()}

            {/* Midpoint dot on edge (mouseDown = add vertex, shift+click = bend, right-click = split) */}
            {/* Hidden when edge has curvature — the curvature control dot handles that case */}
            {canDrag && !edge.curvature && (() => {
              const edgeDx = to.x - from.x;
              const edgeDy = to.y - from.y;
              const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
              if (edgeLen < 5) return null;

              const mx = localOffsetX + edgeDx / 2;
              const my = localOffsetY + edgeDy / 2;

              return (
                <>
                  {/* Small edge indicator line */}
                  <Line
                    points={[localOffsetX, localOffsetY, localOffsetX + edgeDx, localOffsetY + edgeDy]}
                    stroke="#3b82f6"
                    strokeWidth={Math.max(0.5, 1 / scale)}
                    opacity={0.25}
                    listening={false}
                  />
                  {/* Midpoint dot */}
                  <Circle
                    x={mx}
                    y={my}
                    radius={midpointRadius}
                    fill="#fff"
                    stroke="#3b82f6"
                    strokeWidth={Math.max(0.5, 1 / scale)}
                    hitStrokeWidth={Math.max(6, 10 / scale)}
                    onMouseDown={handleMidpointMouseDown(edgeIdx)}
                    onTap={handleMidpointMouseDown(edgeIdx)}
                    onContextMenu={handleMidpointRightClick(edgeIdx)}
                  />
                </>
              );
            })()}
          </React.Fragment>
        );
      })}

      {/* Round line caps at path endpoints */}
      {!closed && lineCapStart === 'round' && vertices.length > 0 && (
        <Circle
          x={vertices[0].x - pathShape.x}
          y={vertices[0].y - pathShape.y}
          radius={thickness / 2}
          fill={color}
          listening={false}
        />
      )}
      {!closed && lineCapEnd === 'round' && vertices.length > 1 && (
        <Circle
          x={vertices[vertices.length - 1].x - pathShape.x}
          y={vertices[vertices.length - 1].y - pathShape.y}
          radius={thickness / 2}
          fill={color}
          listening={false}
        />
      )}

      {/* Rounded corner arcs at vertices */}
      {effectiveVertices.map((vertex, vIdx) => {
        const cr = pathShape.vertices[vIdx]?.cornerRadius;
        if (!cr) return null;

        // Find the two edges meeting at this vertex
        const prevEdgeIdx = closed
          ? (vIdx - 1 + effectiveVertices.length) % effectiveVertices.length
          : vIdx - 1;
        const hasPrev = closed || vIdx > 0;
        const hasNext = closed || vIdx < effectiveVertices.length - 1;
        if (!hasPrev || !hasNext) return null;

        const prevVertex = effectiveVertices[prevEdgeIdx];
        const nextVertex = effectiveVertices[(vIdx + 1) % effectiveVertices.length];

        // Direction vectors from vertex to neighbors
        const toPrevDx = prevVertex.x - vertex.x;
        const toPrevDy = prevVertex.y - vertex.y;
        const toPrevLen = Math.sqrt(toPrevDx * toPrevDx + toPrevDy * toPrevDy);
        const toNextDx = nextVertex.x - vertex.x;
        const toNextDy = nextVertex.y - vertex.y;
        const toNextLen = Math.sqrt(toNextDx * toNextDx + toNextDy * toNextDy);

        if (toPrevLen === 0 || toNextLen === 0) return null;

        // Clamp corner radius to half the shortest adjacent edge
        const maxR = Math.min(toPrevLen / 2, toNextLen / 2);
        const r = Math.min(cr, maxR);

        // Points where the arc starts/ends (offset from vertex along each edge)
        const p1x = vertex.x + (toPrevDx / toPrevLen) * r;
        const p1y = vertex.y + (toPrevDy / toPrevLen) * r;
        const p2x = vertex.x + (toNextDx / toNextLen) * r;
        const p2y = vertex.y + (toNextDy / toNextLen) * r;

        const ox = -pathShape.x;
        const oy = -pathShape.y;
        const cpx = vertex.x + ox;
        const cpy = vertex.y + oy;

        return (
          <Shape
            key={`corner-arc-${vIdx}`}
            sceneFunc={(ctx, s) => {
              ctx.beginPath();
              ctx.moveTo(p1x + ox, p1y + oy);
              ctx.quadraticCurveTo(cpx, cpy, p2x + ox, p2y + oy);
              ctx.strokeShape(s);
            }}
            stroke={color}
            strokeWidth={thickness}
            lineCap="round"
            listening={false}
          />
        );
      })}

      {/* Merge magnets — visible while the user holds Cmd/Ctrl and is
       *  dragging an endpoint of this open wall-path. Each marker sits at
       *  another open wall-path's endpoint; releasing the drag over one
       *  will snap-merge the two paths via autoMergeWallPaths. */}
      {canDrag &&
        cmdHeld &&
        dragVertexIndex !== null &&
        (dragVertexIndex === 0 || dragVertexIndex === vertices.length - 1) &&
        !closed &&
        mergeCandidates.map((c, i) => {
          const isActive =
            !!snapMergeTarget &&
            snapMergeTarget.x === c.x &&
            snapMergeTarget.y === c.y &&
            snapMergeTarget.pathId === c.pathId;
          return (
            <Circle
              key={`merge-magnet-${i}`}
              x={c.x - pathShape.x}
              y={c.y - pathShape.y}
              radius={Math.max(4, (isActive ? 9 : 7) / scale)}
              fill={isActive ? "#10b981" : "#10b98133"}
              stroke="#10b981"
              strokeWidth={Math.max(0.5, 1.5 / scale)}
              listening={false}
            />
          );
        })}

      {/* Vertex handles (when selected) */}
      {canDrag &&
        effectiveVertices.map((vertex, i) => (
          <React.Fragment key={`vertex-${i}`}>
            <Circle
              x={vertex.x - pathShape.x}
              y={vertex.y - pathShape.y}
              radius={vertexRadius}
              fill={dragVertexIndex === i ? "#2563eb" : wallPathSelectedVertexIndex === i ? "#f59e0b" : "#fff"}
              stroke={wallPathSelectedVertexIndex === i ? "#f59e0b" : "#2563eb"}
              strokeWidth={vertexStroke}
              draggable
              onDragStart={handleVertexDragStart(i)}
              onDragMove={handleVertexDragMove(i)}
              onDragEnd={handleVertexDragEnd(i)}
              onClick={handleVertexClick(i)}
              onDblClick={handleVertexRemove(i)}
              onDblTap={handleVertexRemove(i)}
            />
            {/* 1px center dot for precision */}
            <Circle
              x={vertex.x - pathShape.x}
              y={vertex.y - pathShape.y}
              radius={Math.max(0.5, 0.5 / scale)}
              fill={dragVertexIndex === i ? "#fff" : "#2563eb"}
              listening={false}
            />
          </React.Fragment>
        ))}

      {/* Green snap alignment guide lines during vertex drag */}
      {dragVertexIndex !== null && dragAlignX !== undefined && (
        <Line
          x={dragAlignX - pathShape.x}
          points={[0, -10000, 0, 10000]}
          stroke="#00ff00"
          strokeWidth={Math.max(0.5, 1 / scale)}
          dash={[Math.max(1, 4 / scale), Math.max(1, 4 / scale)]}
          listening={false}
        />
      )}
      {dragVertexIndex !== null && dragAlignY !== undefined && (
        <Line
          y={dragAlignY - pathShape.y}
          points={[-10000, 0, 10000, 0]}
          stroke="#00ff00"
          strokeWidth={Math.max(0.5, 1 / scale)}
          dash={[Math.max(1, 4 / scale), Math.max(1, 4 / scale)]}
          listening={false}
        />
      )}
    </Group>
  );
});

WallPathShapeComponent.displayName = "WallPathShape";
